import { Component, Input, ViewChild } from "@angular/core";
import { GridComponent, IGridColumn, IGridSettings, ITableSettings } from "@features/grid";
import { QuerySourceService } from "@main/reporting.v3/api/query-source.service";
import { IQueryCommand } from "@features/filter";
import { IPagedGridSettings, IQuerySchema } from "../../core/reporting-models";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { PlayerService } from "@project/player/player.service";
import { MessageService } from "primeng/api";
import { Message } from "@bbwt/classes";
import { AccountStatus } from "@main/users";
import { UserHelperService } from "@main/users/user-helper.service";


@Component({
    selector: "query-preview",
    templateUrl: "./query-preview.component.html",
    styleUrls: ["query-preview.component.scss"]
})
export class QueryPreviewComponent {
    // General settings.
    tableSettings: ITableSettings;
    gridSettings: IGridSettings;

    private _querySourceId: string;

    @Input() standalone: boolean;

    @ViewChild(GridComponent) private grid: GridComponent;

    constructor(private querySourceService: QuerySourceService,
                private patientService: PatientService,
                private playerService: PlayerService,
                private messageService: MessageService,
                private userHelperService: UserHelperService,
                broadcastService: BroadcastService) {
        broadcastService
            .on(PatientService.CurrentPatientChangedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(_ => this.grid.reload());
    }

    @Input() set querySourceId(value: string) {
        if (!value) return;
        this._querySourceId = value;
        this.refreshGrid().then();
    }

    get querySourceId(): string {
        return this._querySourceId;
    }

    // Refreshing methods.
    async refreshGrid(): Promise<void> {
        // Declare grid columns using query schema.
        const querySchema: IQuerySchema = await this.querySourceService.getQuerySchema(this.querySourceId);

        if (!querySchema?.columns?.length) return;
        const hasPatientId = querySchema.columns.some(c =>
            c.queryAlias.toLowerCase().endsWith("patientid")
        );
        // Reload the grid.
        this.tableSettings = {
            columns: querySchema?.columns.map(column => <IGridColumn>{
                field: column.queryAlias,
                header: column.queryAlias,
                displayHandler: (_, rowValue) => {
                    const value = rowValue[column.queryAlias];

                    if (column.queryAlias.toLowerCase().endsWith("patientid") && typeof value === "string") {
                        return value.split("-")[0];
                    }

                    return value;
                }
            }),
            selectionMode: hasPatientId ? "single" : null
        };

        this.gridSettings = {
            dataService: this,
            dataServiceGetPageMethodName: this.requestData.name,
            readonly: true
        };

        // Reset grid state to clean configurations made over old columns.
        this.grid?.reset();
    }

    private requestData(queryCommand: IQueryCommand) {
        const gridSettings: IPagedGridSettings = {
            skip: queryCommand.skip,
            take: queryCommand.take,
            sortingField: queryCommand.sortingField,
            sortingDirection: queryCommand.sortingDirection
        };

        const queryPageRequest = { gridSettings: gridSettings };
        return this.querySourceService.getQueryPage(this._querySourceId, queryPageRequest);
    }

    protected async onGridRowSelect(event: { data: Record<string, any> }): Promise<void> {
        const row = event.data;
        const keys = Object.keys(row);
        const patientIdKey = keys.find(k => k.toLowerCase().endsWith("patientid"));

        const id = patientIdKey ? row[patientIdKey] : null;

        if (id) {
            try {
                const patientData = await this.playerService.getFromPatientId(id);
                const patientId = patientData?.patientId;
                const allowedStatuses = [
                    AccountStatus.None,
                    AccountStatus.Active
                ];

                if (patientId) {
                    if (patientData.isArchived || !allowedStatuses.includes(patientData.accountStatus)) {
                        this.messageService.add(Message.Info("You do not have permission to view this patient."));
                        return;
                    }

                    const userOrgId = this.userHelperService.organizationId;
                    const isSuperadmin = this.userHelperService.isSuperAdmin;
                    const isMiProAdmin = this.userHelperService.isMiProAdmin;

                    if (!isSuperadmin && !isMiProAdmin) {
                        const hasConsent = patientData?.sharedOrganizations?.some(org => {
                            const orgIdPrefix = org.id.split("-")[0];
                            return orgIdPrefix === userOrgId;
                        });

                        if (!hasConsent) {
                            this.messageService.add(Message.Info("You do not have permission to view this patient."));
                            return;
                        }
                    }

                    const firstName = patientData.firstName;
                    const lastName = patientData.lastName;
                    const fullName = [firstName, lastName].filter(Boolean).join(" ");
                    this.patientService.currentPatientPlaceHolder = { id, fullName };
                } else {
                    this.messageService.add(Message.Info("You do not have permission to view this patient."));
                    return;
                }
            } catch (errorResponse: unknown) {
                this.messageService.add(Message.Info("You do not have permission to view this patient."));
                return;
            }
        } else {
            this.messageService.add(Message.Info("No Patient ID is available for the selected row. Please select a different entry."));
            return;
        }
    }
}
